import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { AnalyzeButton } from './AnalyzeButton';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { useLocale } from '../contexts/LocaleContext';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
  confirmAsAnalyze?: boolean;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  destructive = true,
  confirmAsAnalyze = false,
  confirmLoading = false,
  confirmDisabled = false,
  children,
}) => {
  const { t } = useLocale();
  const [confirmPending, setConfirmPending] = useState(false);
  const isConfirmBusy = confirmLoading || confirmPending;

  useEffect(() => {
    if (!open) {
      setConfirmPending(false);
    }
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isConfirmBusy) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleConfirm = async () => {
    if (isConfirmBusy || confirmDisabled) {
      return;
    }
    setConfirmPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setConfirmPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md z-[2101]" data-confirm-dialog="">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>
        </DialogHeader>
        {children ? <div className="mb-5">{children}</div> : null}
        <DialogFooter>
          <CancelButton onClick={() => handleOpenChange(false)} disabled={isConfirmBusy}>
            {cancelLabel}
          </CancelButton>
          {confirmAsAnalyze ? (
            <AnalyzeButton
              onClick={() => {
                void handleConfirm();
              }}
              isAnalyzing={isConfirmBusy}
              disabled={confirmDisabled}
              dialogPrimary
              className="mr-0"
            >
              {confirmLabel ?? t('analyze')}
              {!isConfirmBusy ? <DialogPrimaryShortcut className="ml-0.5" /> : null}
            </AnalyzeButton>
          ) : (
            <Button
              type="button"
              variant={destructive ? 'destructive' : 'default'}
              dialogPrimary
              className="group inline-flex items-center gap-1.5"
              disabled={isConfirmBusy || confirmDisabled}
              onClick={() => {
                void handleConfirm();
              }}
            >
              {destructive ? <RotatingDeleteIcon /> : null}
              {confirmLabel ?? t('delete_resource')}
              <DialogPrimaryShortcut className="ml-0.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
